/**
 * The agents a session may run (§5.10) — the CLIs Airlock knows how to launch,
 * drive and read the status of. Two today: Claude Code and Codex.
 *
 * An agent is **implied by the model**, never stored beside it: the model ids of
 * the two CLIs are disjoint (`opus` is Claude's, `gpt-5.5` is Codex's), so one
 * grammar answers both questions and a row can never carry a model one agent
 * has and the name of the other (§9.3). What *is* recorded is which agent a live
 * session is currently being driven through, because a workflow may switch
 * mid-run (§5.15) and a restart has to find the same terminal again.
 */
import type { Choice } from './models';

export type AgentId = 'claude-code' | 'codex';

export const AGENTS: (Choice & { value: AgentId })[] = [
  { value: 'claude-code', label: 'Claude Code' },
  { value: 'codex', label: 'Codex' }
];

export const AGENT_IDS: AgentId[] = AGENTS.map((a) => a.value);

/** What everything written before a second agent existed reads back as. */
export const DEFAULT_AGENT: AgentId = 'claude-code';

export function isAgentId(value: unknown): value is AgentId {
  return typeof value === 'string' && (AGENT_IDS as string[]).includes(value);
}

/** A stored value as an agent, or the default when it is not one. */
export function agentOf(value: unknown): AgentId {
  return isAgentId(value) ? value : DEFAULT_AGENT;
}

export function agentLabel(id: AgentId): string {
  return AGENTS.find((a) => a.value === id)?.label ?? id;
}

/**
 * Which terminal of kind `agent` an agent's TUI lives in (§5.3). One slot per
 * agent rather than one per launch, so the second agent a workflow switches to
 * is a second tmux session beside the first, and switching back finds the first
 * still there with its conversation intact.
 */
export function agentSlot(id: AgentId): number {
  return AGENT_IDS.indexOf(id);
}
